import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { History, Save, ChevronRight, Clock } from 'lucide-react';
import { useProjectLibrary } from '@/entities/estimate';
import { ProjectSnapshot } from '@/shared/model';

interface VersionHistoryProps {
    activeProjectId?: string | number | null;
    commitVersion?: (name: string) => void;
    onLoadSnapshot?: (snapshot: ProjectSnapshot) => void;
}

export const VersionHistory: FC<VersionHistoryProps> = ({
    activeProjectId,
    commitVersion,
    onLoadSnapshot
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { library } = useProjectLibrary();

    const activeProjectFromLib = library.find(p => String(p.id) === String(activeProjectId));
    const history = activeProjectFromLib?.history || [];

    const [isSavingVersion, setIsSavingVersion] = useState(false);
    const [versionName, setVersionName] = useState('');

    const handleSaveVersion = () => {
        if (versionName.trim() && commitVersion) {
            commitVersion(versionName);
            setVersionName('');
            setIsSavingVersion(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2 flex items-center gap-2">
                <History size={12}/> {t('editor.sidebar.history', 'История версий')}
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 space-y-4">
                {!activeProjectId ? (
                    <div className="text-center py-2">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                            {t('editor.sidebar.enable_history_desc', 'Сохраните проект в библиотеку, чтобы включить автосохранение и историю версий.')}
                        </p>
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="w-full py-2 bg-blue-600 text-white rounded-xl text-[10px] font-bold flex items-center justify-center gap-2"
                        >
                            <Save size={12}/> {t('editor.sidebar.enable_history', 'ВКЛЮЧИТЬ ИСТОРИЮ')}
                        </button>
                    </div>
                ) : (
                    <>
                        {!isSavingVersion ? (
                            <button 
                                onClick={() => setIsSavingVersion(true)}
                                className="w-full py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/50 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-blue-100 transition-all"
                            >
                                <Save size={14}/> {t('editor.sidebar.save_point', 'СОЗДАТЬ ТОЧКУ')}
                            </button>
                        ) : (
                            <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                <input 
                                    autoFocus
                                    value={versionName}
                                    onChange={(e) => setVersionName(e.target.value)}
                                    placeholder={t('editor.sidebar.version_name', 'Имя версии...')}
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-blue-500 rounded-xl text-xs font-bold outline-none"
                                    aria-label={t('editor.sidebar.version_name', 'Имя версии...')}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveVersion()}
                                />
                                <div className="flex gap-2">
                                    <button 
                                        onClick={handleSaveVersion} 
                                        className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-[10px] font-bold"
                                    >
                                        {t('common.save', 'СОХРАНИТЬ')}
                                    </button>
                                    <button 
                                        onClick={() => setIsSavingVersion(false)} 
                                        className="px-3 py-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg text-[10px] font-bold"
                                    >
                                        {t('common.cancel', 'ОТМЕНА')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="space-y-1 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                            {history.length === 0 ? (
                                <div className="text-[10px] text-slate-500 dark:text-slate-400 text-center py-4 italic">
                                    {t('editor.sidebar.no_history', 'Точек сохранения нет')}
                                </div>
                            ) : (
                                history.map((point: Omit<ProjectSnapshot, 'history'>, idx: number) => (
                                    <button 
                                        key={idx}
                                        onClick={() => onLoadSnapshot?.(point as ProjectSnapshot)}
                                        className="w-full text-left p-2.5 bg-white dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 transition-all group"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="font-bold text-[11px] text-slate-700 dark:text-slate-200 line-clamp-1 group-hover:text-blue-600 transition-colors">
                                                {point.historyName || t('editor.sidebar.auto_save', 'Автосохранение')}
                                            </div>
                                            <ChevronRight size={12} className="text-slate-300 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[9px] text-slate-400 font-medium mt-1 uppercase">
                                            <Clock size={10}/>{' '}
                                            <span className="font-mono tabular-nums">{point.timestamp ? new Date(point.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
